import { useContext, useEffect, useMemo, useRef, useState } from "react";
import useTranslator from "../../hooks/useTranslator";
import TenantContext from "../../context/tenant/TenantContext";
import constants from "../../Utils/constants";
import docsData from "../../Utils/docsData";
import {
  getDocumentsFromEmploymentType,
  haveValue,
  toast,
  getRequiredPayslipPeriods,
  getRequiredVatQuarterPeriods,
  getPayslipDateWarning,
  isPayslipWithinRequiredPeriod,
  removeUuidFromFileName,
  isCompanyTenantOwnDocument,
} from "../../helper/common";

import DocItem from "../../components/Onboarding/DocItem";
import SkorDefaultLoader from "../../assets/img/skorDefaultLoader.gif";
import ApprovedModal from "./approvedModal";
import DocumentAnalysisLoadingPopup from "../../components/Onboarding/DocumentAnalysisLoading";
import { docsMap } from "../../Utils/skorLiteDocumentMap";
import { useNavigate } from "react-router-dom";
import PermissoWidget from "./PermisssionWidget";
import * as pdfjsLib from "pdfjs-dist/webpack";
import { trackClarityEvent } from "../../Utils/clarityReady";
import AuthContext from "../../context/auth/AuthContext";
import ButtonActionTooltip from "../../components/ErrorModal/ButtonActionTooltip";
import DisableZoom from "../../helper/disableZoom";
import styles from "./RequiredDocumentsModal.module.css";
import * as Sentry from "@sentry/react";
import {
  isKycReviewFile as isKycReviewFileUtil,
  isKycTenantDoc,
} from "../../Utils/kycUtils";
import {
  FileInput,
  Badge,
  Button,
  Icon,
  Skeleton,
  useInlineStep,
} from "../../components/design";
import ClaveModal from "../../components/ClaveModal/ClaveModal";

// Company login tenants: one Form 303 slot per required quarter, in the order
// of getRequiredVatQuarterPeriods (most recent first).
const VAT_QUARTER_KEYS = [
  constants.firstQuarterVat,
  constants.secondQuarterVat,
  constants.thirdQuarterVat,
];

const PAYSLIP_PERIOD_KEYS = [
  constants.firstMonthPayslip,
  constants.secondMonthPayslip,
  constants.thirdMonthPayslip,
];

const REVIEW_TAGS = {
  scan: "fileReviewTagScan",
  duplicate: "fileReviewTagDuplicate",
  thirdParty: "fileReviewTagThirdParty",
};

// Telemetry must never interfere with the upload/validation flow.
const reportUploadIssue = (issue, context = {}, level = "error") => {
  try {
    Sentry.withScope((scope) => {
      scope.setTag("feature", "onboarding-document-upload");
      if (context.stage) scope.setTag("upload_stage", context.stage);
      if (context.validationReason) {
        scope.setTag("validation_reason", context.validationReason);
      }
      scope.setLevel(level);
      scope.setContext("upload", context);

      if (issue instanceof Error) {
        Sentry.captureException(issue);
      } else {
        Sentry.captureMessage(String(issue), level);
      }
    });
  } catch {
    // Sentry failures must not interrupt the user's upload flow.
  }
};

const getFileMetadata = (file) => ({
  mimeType: file?.type || "unknown",
  sizeBytes: Number.isFinite(file?.size) ? file.size : null,
});

const getFileNameFromUrl = (url) => {
  if (!haveValue(url) || typeof url !== "string") return "";

  try {
    return new URL(url).pathname.split("/").filter(Boolean).pop() ?? "";
  } catch {
    return url.split("?")[0].split("/").filter(Boolean).pop() ?? "";
  }
};

const isValidPayslipPeriod = (year, month) =>
  Number.isInteger(year) &&
  year > 0 &&
  Number.isInteger(month) &&
  month >= 1 &&
  month <= 12;

const getProfileDocuments = (profile) => {
  const buckets = profile?.report?.documentBuckets ?? [];

  if (buckets.length > 0) {
    return buckets.flatMap((bucket) =>
      (bucket?.documents ?? []).map((doc) => ({
        ...doc,
        bucketName: bucket?.bucketName ?? doc?.bucketName,
      }))
    );
  }

  return profile?.report?.allDocuments ?? [];
};

// KYC / identification docs must never count as analysis for Next gating.
// Mirrors kycUtils but works on the analysis-doc shape (bucketName/documentName/url).
const isKycAnalysisDoc = (doc) => {
  if (!doc) return false;
  const bucket = (doc?.bucketName ?? "").toString().toLowerCase();
  if (bucket === "identification") return true;
  if (
    bucket.includes("passport") ||
    bucket.includes("national identity") ||
    bucket.includes("residence permit")
  )
    return true;
  const name = (
    doc?.localDocumentName?.en ||
    doc?.documentNameTranslated?.en ||
    doc?.documentName ||
    ""
  )
    .toString()
    .toLowerCase();
  if (
    name.includes("passport") ||
    name.includes("national identity") ||
    name.includes("identity card") ||
    name.includes("residence permit") ||
    name.includes("dni/nie") ||
    name === "dni" ||
    name === "nie" ||
    name === "other identity document"
  )
    return true;
  const fileName = getFileNameFromUrl(doc?.data?.url ?? doc?.url ?? "")
    .toString()
    .toLowerCase();
  // KYC images: kycdoc__* (current), *onfido* (saved before the Didit switch).
  if (fileName.includes("kycdoc__") || fileName.includes("onfido")) return true;
  return false;
};

const buildFilesToReview = (documentAnalysisResponse, user) => {
  const profiles =
    documentAnalysisResponse?.document_analysis?.profileReports ??
    documentAnalysisResponse?.profileReports ??
    [];
  const forensicReports =
    user?.tenant_extra_field_id?.forensic_analysis?.profileReports ?? [];
  const scannedFileNames = new Set();

  forensicReports.forEach((entry) => {
    (entry?.report?.documents_list ?? []).forEach((doc) => {
      if (
        doc?.documentType === "is_scanned_document" ||
        doc?.isScannedDocument === "yes"
      ) {
        scannedFileNames.add(getFileNameFromUrl(doc?.url));
      }
    });
  });

  return profiles.flatMap((profile) => {
    const isUploader = profile?.data?.is_uploader;
    const ownerName = (
      profile?.data?.owner ??
      profile?.owner ??
      profile?.data?.owner_name ??
      profile?.data?.name ??
      ""
    )
      .toString()
      .trim();
    const ownerType = profile?.data?.owner_type || profile?.owner_type;

    return getProfileDocuments(profile).flatMap((doc) => {
      const url = doc?.data?.url ?? doc?.url;
      const fileName = getFileNameFromUrl(url) || doc?.documentName;
      let tag = null;

      if (doc?.isDuplicate) tag = "duplicate";
      else if (
        (!isUploader || ownerType === "legal_entity") &&
        !isCompanyTenantOwnDocument(user, { isUploader, doc })
      )
        tag = "thirdParty";
      else if (scannedFileNames.has(fileName)) tag = "scan";

      return fileName && tag
        ? [
            {
              id: `${ownerName || "unknown"}-${fileName}-${doc?.documentId ?? doc?.url}`,
              fileName,
              tag,
              ownerName,
              documentName:
                doc?.localDocumentName?.en ||
                doc?.documentNameTranslated?.en ||
                doc?.documentName,
            },
          ]
        : [];
    });
  });
};

const RequiredDocumentsModal = ({
  user,
  formHandler,
  updateUserProfile,
  isGuarantor,
  isCoTenant = false,
  isFirstTime = false,
  guarantor_a_id,
  co_tenant_a_id,
  reqFor,
  onClose,
  redirectUrl,
  setDocAnalysisLoading,
  isOpen,
  reloadTrigger,
  needWaitingJobForClave,
  isEditMode,
  fetchFileDataParent,
  modules,
  handleSubmitFinalStep,
  isDummySignup,
  setStep,
  isFormSubmitted,
  setIsFormSubmitted,
  documentAnalysisResponse,
  refreshDocumentAnalysis,
  hideClave = false,
  llCreated = false,
  requiresSubcategory,
  deletedReviewFileIds: sharedDeletedReviewFileIds,
  setDeletedReviewFileIds: setSharedDeletedReviewFileIds,
}) => {
  const requiresSubcategoryFn =
    requiresSubcategory ??
    ((s) =>
      [
        constants.public_servant,
        constants.employed,
        constants.self_employed,
      ].includes(s));
  const isKycDone = user?.onfido_user_kyc?.server_status === "completed";
  const fileInputRef = useRef(null);
  const { _t } = useTranslator();
  // onboarding page (not a popup): Cl@ve opens in the page, not over it
  const inlineStep = useInlineStep();
  const navigate = useNavigate();
  const {
    initiateBankFlip,
    fetchTenantDocs,
    uploadFileData,
    fetchWaitingData,
    detectDocument,
    skipDuplicateDocument,
    deleteDuplicateDocuments,
    deleteDocument,
  } = useContext(TenantContext);
  const { loadUserTenant } = useContext(AuthContext);
  const [docUploadLoading, setDocUploadLoading] = useState(false);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showApprovalPopup, setShowApprovalPopup] = useState(false);
  const [availableClaveDocs, setAvailableClaveDocs] = useState([]);
  const [tenantDocsData, setTenantDocsData] = useState([]);
  const [documentLoading, setDocumentLoading] = useState(true);
  const [waitingJobLoading, setWaitingJobLoading] = useState(false);
  // Clave import or a delete on this screen changed the documents, so
  // Continue must detect them again. Without a change it only moves on.
  const [docsChangedHere, setDocsChangedHere] = useState(false);
  const queryParameters = new URLSearchParams(window.location.search);
  const waitingId = queryParameters.get("waiting");
  const [waitingJobId, setWaitingJobId] = useState(null);
  // got from clave as a redirect url
  const welcome = queryParameters.get("welcome");
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [claveWidgetUrl, setClaveWidgetUrl] = useState(null);
  const [claveInitiating, setClaveInitiating] = useState(false);
  const [invalidFiles, setInvalidFiles] = useState([]);
  const [deletingFileId, setDeletingFileId] = useState(null);
  const [localDeletedReviewFileIds, setLocalDeletedReviewFileIds] = useState(
    []
  );
  const deletedReviewFileIds =
    sharedDeletedReviewFileIds ?? localDeletedReviewFileIds;
  const setDeletedReviewFileIds =
    setSharedDeletedReviewFileIds ?? setLocalDeletedReviewFileIds;
  const [isDeletingAllReview, setIsDeletingAllReview] = useState(false);
  const [showClaveModal, setShowClaveModal] = useState(false);
  // Shared analysis comes from OnboardingMain (single fetch + cache).
  // This component only consumes it and invalidates on new upload/delete.
  // Fallback: the updateDoc path (tenant profile / skorList) never passes the
  // prop, but the user object already embeds document_analysis — use it so
  // payslip period matching works there too.
  const loadedAnalysisResponse = useMemo(() => {
    if (documentAnalysisResponse) return documentAnalysisResponse;
    const embeddedAnalysis =
      user?.tenant_extra_id?.document_analysis ??
      user?.tenant_extra_field_id?.document_analysis ??
      null;
    if (embeddedAnalysis) return { document_analysis: embeddedAnalysis };
    return {};
  }, [
    documentAnalysisResponse,
    user?.tenant_extra_id?.document_analysis,
    user?.tenant_extra_field_id?.document_analysis,
  ]);

  const handleClaveNext = () => {
    setShowClaveModal(false);
  };

  const hasDocumentAnalysis = useMemo(() => {
    const profiles =
      loadedAnalysisResponse?.document_analysis?.profileReports ??
      loadedAnalysisResponse?.profileReports ??
      [];
    if (!Array.isArray(profiles) || profiles.length === 0) return false;
    // KYC-only analysis must not unlock Next — require at least one non-KYC doc.
    return profiles.some((profile) =>
      getProfileDocuments(profile).some((doc) => !isKycAnalysisDoc(doc))
    );
  }, [loadedAnalysisResponse]);
  const isOnboardingNext = reqFor !== "updateDoc";
  const canProceedWithAnalysis =
    hasDocumentAnalysis && isOnboardingNext && !user?.re_new_pending;

  useEffect(() => {
    setShowWelcomePopup(welcome === "true");
  }, [queryParameters]);

  // ✅ File Validation Helper
  const validateFiles = async (newFiles) => {
    const validatedFiles = [];
    const invalidFilesTemp = [];

    for (let file of newFiles) {
      // 🔹 File type check
      if (
        !["application/pdf", "image/jpeg", "image/png", "image/jpg"].includes(
          file.type
        )
      ) {
        reportUploadIssue(
          "File rejected by frontend validation: unsupported file type",
          {
            stage: "file-validation",
            validationReason: "unsupported-file-type",
            ...getFileMetadata(file),
          },
          "warning"
        );
        invalidFilesTemp.push({
          name: file.name,
          reason: _t("fileTypeNotAllowed", "File type not allowed"),
        });
        continue;
      }

      // 🔹 File size check (8 MB)
      if (file.size > 8 * 1024 * 1024) {
        reportUploadIssue(
          "File rejected by frontend validation: file exceeds per-file size limit",
          {
            stage: "file-validation",
            validationReason: "file-too-large",
            maxSizeBytes: 8 * 1024 * 1024,
            ...getFileMetadata(file),
          },
          "warning"
        );
        invalidFilesTemp.push({
          name: file.name,
          reason: _t(
            "fileUploadedExceedsTwoPointFiveMbFileLimit",
            "File exceeds 4MB limit"
          ),
        });
        continue;
      }

      try {
        if (file.type === "application/pdf") {
          const arrayBuffer = await file.arrayBuffer();
          await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        } else {
          // 🔹 Validate image
          await new Promise((resolve, reject) => {
            const img = new Image();
            const objectUrl = URL.createObjectURL(file);
            img.onload = () => {
              URL.revokeObjectURL(objectUrl);
              resolve();
            };
            img.onerror = () => {
              URL.revokeObjectURL(objectUrl);
              reject(new Error("Corrupted image"));
            };
            img.src = objectUrl;
          });
        }

        validatedFiles.push(file);
      } catch (err) {
        let reason = _t(
          "isCorruptedOrUnreadableFile",
          "is corrupted or unreadable"
        );
        if (err?.message?.toLowerCase()?.includes("password")) {
          reason = _t("isPasswordProtectedPDF", "is password protected");
        }

        invalidFilesTemp.push({ name: file.name, reason });
        reportUploadIssue(
          err,
          {
            stage: "file-content-validation",
            validationReason: "unreadable-or-corrupted-file",
            ...getFileMetadata(file),
          },
          "warning"
        );
      }
    }

    // ✅ Append invalid files instead of replacing
    if (invalidFilesTemp.length > 0) {
      setInvalidFiles((prev) => [...prev, ...invalidFilesTemp]);
    }

    // ✅ Return validated files so you can append them in parent logic
    return validatedFiles;
  };

  const startWaitingJobInterval = (waitingId) => {
    if (!haveValue(waitingId)) return;

    setWaitingJobLoading(true);

    const temp_interval = setInterval(() => {
      fetchWaitingData(waitingId)
        .then((res) => {
          if (res.status === constants.COMPLETED) {
            clearInterval(temp_interval);
            setDocsChangedHere(true);

            // Doing this for tenant for approval popup to get latest clave done value.
            if (!isGuarantor && !isCoTenant) {
              loadUserTenant("tenant");
            }
            fetchFileData(res?.tenant_id);
            fetchFileDataParent?.();
            refreshDocumentAnalysis?.();
            setWaitingJobLoading(false);

            // Remove 'waiting' param from URL
            const params = new URLSearchParams(window.location.search);
            params.delete("waiting");
            const newUrl =
              window.location.pathname +
              (params.toString() ? `?${params.toString()}` : "");
            window.history.replaceState({}, "", newUrl);
          }
        })
        .catch((err) => {
          console.error("❌ Waiting job error:", err);
          setWaitingJobLoading(false);
          clearInterval(temp_interval);
        });
    }, 1000);

    return temp_interval;
  };

  useEffect(() => {
    if (haveValue(waitingId)) {
      const intervalId = startWaitingJobInterval(waitingId);

      return () => clearInterval(intervalId);
    }
  }, []);

  useEffect(() => {
    fetchFileData();
  }, [reloadTrigger, isOpen]);

  const handleClick = () => {
    fileInputRef.current.click();
  };

  const handleUploadFiles = async (
    fileType = "uploaded-from-onboarding",
    fileFrom
  ) => {
    if (
      docFound.size === 0 &&
      selectedFiles.length === 0 &&
      !canProceedWithAnalysis
    ) {
      toast.error(
        _t("pleaseUploadDocumentsToContinue", "Text used in onboarding flow")
      );
      return;
    }
    if (user?.re_new_pending && selectedFiles.length === 0) {
      toast.error(
        _t("pleaseUploadDocumentsToContinue", "Text used in onboarding flow")
      );
      return;
    }
    let uploadStage = "upload-initialization";
    try {
      if (reqFor === "updateDoc") {
        if (selectedFiles.length > 0) {
          setDocAnalysisLoading(true);
        } else {
          onClose();
        }
      } else {
        setDocUploadLoading(true);
        setIsUploadingFiles(selectedFiles.length > 0);
      }
      if (selectedFiles.length > 0) {
        uploadStage = "skip-duplicate-check";
        await skipDuplicateDocument();
      }
      const formData = new FormData();

      for (let file of selectedFiles) {
        formData.append("tenantDocs", file);
      }

      formData.append("fileType", fileType);
      if (fileFrom) formData.append("fileFrom", fileFrom);

      if (selectedFiles.length > 0) {
        uploadStage = "file-upload";
        const uploadResponse = await uploadFileData(
          formData,
          llCreated,
          user?._id,
          isGuarantor || isCoTenant
        );

        if (
          uploadResponse?.status !== 200 ||
          uploadResponse?.data?.success !== true
        ) {
          reportUploadIssue(
            "Document upload returned an unsuccessful response",
            {
              stage: uploadStage,
              responseStatus: uploadResponse?.status ?? null,
              responseSuccess: uploadResponse?.data?.success ?? null,
              fileCount: selectedFiles.length,
              files: selectedFiles.slice(0, 10).map(getFileMetadata),
              filesTruncated: selectedFiles.length > 10,
              fileType,
              fileFrom: fileFrom ?? null,
              reqFor: reqFor ?? null,
            }
          );
          toast.error(_t("errorInUploadingFile"));
          if (reqFor === "updateDoc") {
            setDocAnalysisLoading(false);
          } else {
            setDocUploadLoading(false);
          }
          return;
        }

        setSelectedFiles([]);
        // New docs invalidate the shared analysis cache -> parent repolls once.
        refreshDocumentAnalysis?.();
        if (reqFor === "updateDoc") {
          setDocAnalysisLoading(false);
          onClose();
          fetchFileDataParent?.();
          // If we are waiting for documents to upload
        } else {
          // Hand off analysis loading to MissingDocument (step 4) — avoid
          // showing two DocumentAnalysisLoadingPopup at once (RequiredDocuments
          // and MissingDocument both render one).
          //   setDocUploadLoading(false);
          // removing clavedone field
          const url = new URL(window.location);
          url.searchParams.delete("claveDone");
          window.history.replaceState(
            {},
            document.title,
            url.pathname + url.search
          );
          // removing clavedone field
          if (isEditMode) {
            markSubmittedFromRequired();
            formHandler?.next?.();
          } else {
            const role = localStorage.getItem("usertype");
            markSubmittedFromRequired();
            uploadStage = "profile-update";
            let step = 4;
            if (Array.isArray(modules)) {
              if (
                modules.includes("DISABLE_KYC") &&
                modules.includes("DISABLE_BANK") &&
                modules.includes("DISABLE_GUARANTORS")
              ) {
                step = -1;
              } else if (
                modules.includes("DISABLE_KYC") &&
                modules.includes("DISABLE_BANK")
              ) {
                step = isGuarantor ? -1 : 7;
              } else if (modules.includes("DISABLE_KYC")) {
                step = 6;
              }
            }
            if (step === -1 && isGuarantor) {
              handleSubmitFinalStep();
            } else {
              let payload = { step: isGuarantor ? 6 : 4, role };
              if (modules?.includes("DISABLE_KYC")) {
                payload.manual_kyc_doc_status = "BYPASS";
              }
              await updateUserProfile(payload);
              if (window.location.pathname === "/onboarding" && !isGuarantor) {
                trackClarityEvent("tenant_document_upload_step_ended");
                setTimeout(() => {
                  trackClarityEvent("tenant_missing_document_step_started");
                }, constants.CLARITY_DELAY);
              }
            }
          }
        }
      } else {
        const role = localStorage.getItem("usertype");
        const url = new URL(window.location);
        // detect only when something is new: no analysis yet, Clave just
        // finished (claveDone in the URL after its redirect), or docs were
        // imported or deleted on this screen
        const needsDetection =
          !hasDocumentAnalysis ||
          docsChangedHere ||
          url.searchParams.get("claveDone") === "true";
        if (needsDetection) {
          uploadStage = "document-detection";
          await detectDocument(user?._id);
          refreshDocumentAnalysis?.();
        }
        // removing clavedone field
        url.searchParams.delete("claveDone");
        window.history.replaceState(
          {},
          document.title,
          url.pathname + url.search
        );
        // removing clavedone field
        if (isEditMode) {
          markSubmittedFromRequired();
          formHandler?.next?.();
        } else {
          markSubmittedFromRequired();
          uploadStage = "profile-update";
          let step = 4;
          if (Array.isArray(modules)) {
            if (
              modules.includes("DISABLE_KYC") &&
              modules.includes("DISABLE_BANK") &&
              modules.includes("DISABLE_GUARANTORS")
            ) {
              step = -1;
            } else if (
              modules.includes("DISABLE_KYC") &&
              modules.includes("DISABLE_BANK")
            ) {
              step = isGuarantor ? -1 : 7;
            } else if (modules.includes("DISABLE_KYC")) {
              step = 6;
            }
          }
          if (step === -1 && isGuarantor) {
            handleSubmitFinalStep();
          } else {
            let payload = { step: isGuarantor ? 6 : 4, role };
            if (modules?.includes("DISABLE_KYC")) {
              payload.manual_kyc_doc_status = "BYPASS";
            }
            await updateUserProfile(payload);
            if (window.location.pathname === "/onboarding" && !isGuarantor) {
              trackClarityEvent("tenant_document_upload_step_ended");
              setTimeout(() => {
                trackClarityEvent("tenant_missing_document_step_started");
              }, constants.CLARITY_DELAY);
            }
          }
        }
      }
    } catch (err) {
      reportUploadIssue(err, {
        stage: uploadStage,
        fileCount: selectedFiles.length,
        files: selectedFiles.slice(0, 10).map(getFileMetadata),
        filesTruncated: selectedFiles.length > 10,
        fileType,
        fileFrom: fileFrom ?? null,
        reqFor: reqFor ?? null,
        isGuarantor: Boolean(isGuarantor),
        isCoTenant: Boolean(isCoTenant),
        llCreated: Boolean(llCreated),
      });
      console.error("Error uploading multiple files:", err);
      if (reqFor === "updateDoc") {
        setDocAnalysisLoading(false);
      } else {
        setDocUploadLoading(false);
      }
    } finally {
      if (reqFor === "updateDoc") {
        setDocAnalysisLoading(false);
      } else {
        setDocUploadLoading(false);
        setIsUploadingFiles(false);
      }
    }
  };

  const processFiles = async (newFiles) => {
    if (!Array.isArray(newFiles)) return;

    try {
      const validFiles = await validateFiles(newFiles);
      if (validFiles.length === 0) return;

      // Total size check (existing + new)
      const existingSize = selectedFiles.reduce((sum, f) => sum + f.size, 0);
      const newSize = validFiles.reduce((sum, f) => sum + f.size, 0);
      const totalSize = existingSize + newSize;

      if (totalSize > 20 * 1024 * 1024) {
        reportUploadIssue(
          "Files rejected by frontend validation: combined size exceeds limit",
          {
            stage: "file-validation",
            validationReason: "combined-files-too-large",
            existingFileCount: selectedFiles.length,
            newFileCount: validFiles.length,
            existingSizeBytes: existingSize,
            newSizeBytes: newSize,
            totalSizeBytes: totalSize,
            maxTotalSizeBytes: 20 * 1024 * 1024,
          },
          "warning"
        );
        toast.error(_t("totalFileSizeExceeds20MB"));
        return;
      }

      setSelectedFiles((prev) => [...prev, ...validFiles]);
    } catch (err) {
      reportUploadIssue(err, {
        stage: "file-validation",
        validationReason: "validation-processing-failed",
        fileCount: newFiles.length,
      });
    }
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFiles(Array.from(files));
    }
    e.target.value = "";
  };

  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();

    dragCounter.current += 1;
    if (dragCounter.current === 1) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();

    dragCounter.current -= 1;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    const newFiles = Array.from(files);
    processFiles(newFiles);
  };

  const fetchFileData = async (custom_tenant_id) => {
    setDocumentLoading(true);
    try {
      const tenant_id = haveValue(custom_tenant_id)
        ? custom_tenant_id
        : user?._id;
      const res = await fetchTenantDocs(
        tenant_id,
        llCreated,
        isGuarantor || isCoTenant
      );
      const rawDocs = res?.data?.success_res?.tenantDocs ?? [];
      const filteredDocs = Array.isArray(rawDocs)
        ? rawDocs.filter((doc) => !isKycTenantDoc(doc))
        : [];
      setTenantDocsData(filteredDocs);
    } catch (error) {
      console.error("Error fetching tenant docs:", error);
    } finally {
      setDocumentLoading(false);
    }
  };

  const getRequiredProtectedHousingDocs = () => {
    const selectedProtectedDocs =
      user?.tenant_extra_field_id?.[constants.public_housing_fileFrom] ?? [];

    const baseProtectedDocs = selectedProtectedDocs.filter(
      (doc) =>
        doc &&
        doc !== constants.no_special_circumstances &&
        doc !== "no" &&
        doc?.toLowerCase?.() !== "no special circumstances" &&
        doc !== constants.certificate_of_residency_registration
    );

    const hasNoSpecialCircumstances =
      selectedProtectedDocs.includes(constants.no_special_circumstances) ||
      selectedProtectedDocs.includes("no") ||
      selectedProtectedDocs.some(
        (v) => v?.toLowerCase?.() === "no special circumstances"
      );

    if (baseProtectedDocs.length > 0) {
      return [
        ...baseProtectedDocs,
        constants.certificate_of_residency_registration,
      ];
    }

    if (hasNoSpecialCircumstances) {
      return [constants.certificate_of_residency_registration];
    }

    return [];
  };

  const [otherDocs, setOtherDocs] = useState([]);
  const [payslipDocs, setPayslipDocs] = useState([]);
  const [docFound, setDocFound] = useState(new Map());
  const [haveClavePinDoc, setHaveClavePinDoc] = useState(false);
  const [payslipValidation, setPayslipValidation] = useState(new Map());
  const [requiredPayslipPeriods, setRequiredPayslipPeriods] = useState([]);
  const isThreePayslipRequired = useMemo(
    () =>
      user?.employment_status === constants.employed ||
      user?.employment_status === constants.public_servant,
    [user]
  );

  useEffect(() => {
    if (isThreePayslipRequired) {
      const periods = getRequiredPayslipPeriods();
      setRequiredPayslipPeriods(periods);
    } else {
      setRequiredPayslipPeriods([]);
    }
  }, [isThreePayslipRequired]);

  useEffect(() => {
    if (haveValue(tenantDocsData) && tenantDocsData.length >= 1) {
      const temp_map = new Map();
      const type_map = new Map();
      const other_type = new Map();
      let payslips = [];
      const payslipValidationMap = new Map();

      // The analysis response can be passed either as the complete response
      // (`{ document_analysis: ... }`) or as the inner `document_analysis`
      // object. Keep this lookup consistent with buildFilesToReview so the
      // payslip periods are resolved in both cases.
      const profileReports =
        loadedAnalysisResponse?.document_analysis?.profileReports ??
        loadedAnalysisResponse?.profileReports ??
        [];
      const matchedDocuments = profileReports.flatMap(
        (profile) => profile?.report?.matchedDocuments ?? []
      );

      const documentPeriodMap = new Map();
      for (const doc of matchedDocuments) {
        const filename = getFileNameFromUrl(doc?.data?.url ?? doc?.url);
        const periodYear = Number(doc?.period_year);
        const periodMonth = Number(doc?.period_month);
        if (filename && isValidPayslipPeriod(periodYear, periodMonth)) {
          documentPeriodMap.set(filename, {
            period_year: periodYear,
            period_month: periodMonth,
          });
        }
      }

      // Fallback period source: forensic analysis documents_list carries
      // data.period_year / data.period_month per file. The updateDoc path
      // (skorList) has no matchedDocuments, so without this every payslip
      // has undefined period and can never match a slot. Same source
      // DocumentSection / payslipRequirement use for slot matching.
      const forensicReportsForPeriods =
        user?.tenant_extra_id?.forensic_analysis?.profileReports ??
        user?.tenant_extra_field_id?.forensic_analysis?.profileReports ??
        [];
      const forensicByFile = {};
      const forensicByInstanceId = {};
      (Array.isArray(forensicReportsForPeriods)
        ? forensicReportsForPeriods
        : []
      ).forEach((entry) => {
        (entry?.report?.documents_list ?? []).forEach((doc) => {
          const fileName = getFileNameFromUrl(doc?.url);
          if (fileName && !forensicByFile[fileName]) {
            forensicByFile[fileName] = doc;
          }
          if (doc?.instanceId && !forensicByInstanceId[doc.instanceId]) {
            forensicByInstanceId[doc.instanceId] = doc;
          }
        });
      });
      const resolvePayslipPeriod = (tenantDoc) => {
        const fileName = tenantDoc?.file_name;
        const fromMap = documentPeriodMap.get(fileName);
        if (fromMap) return fromMap;
        const instanceId = tenantDoc?.documentAnalysisData?.instanceId;
        const forensicDoc =
          (instanceId ? forensicByInstanceId[instanceId] : null) ??
          forensicByFile[fileName];
        const periodYear = Number(
          forensicDoc?.data?.period_year ??
            tenantDoc?.documentAnalysisData?.data?.period_year ??
            tenantDoc?.documentAnalysisData?.period_year ??
            tenantDoc?.period_year
        );
        const periodMonth = Number(
          forensicDoc?.data?.period_month ??
            tenantDoc?.documentAnalysisData?.data?.period_month ??
            tenantDoc?.documentAnalysisData?.period_month ??
            tenantDoc?.period_month
        );
        return {
          period_year: isValidPayslipPeriod(periodYear, periodMonth)
            ? periodYear
            : undefined,
          period_month: isValidPayslipPeriod(periodYear, periodMonth)
            ? periodMonth
            : undefined,
        };
      };

      const analysisDocuments = profileReports.flatMap((profile) =>
        getProfileDocuments(profile)
      );
      const duplicateAnalysisFileNames = new Set(
        analysisDocuments
          .filter((doc) => doc?.isDuplicate === true)
          .map((doc) => getFileNameFromUrl(doc?.data?.url ?? doc?.url))
          .filter(Boolean)
      );

      const matchedPeriodIndexes = new Set();

      // Company login tenants: Form 303 files fill the quarter slots by
      // period_quarter; other quarters stay as additional documents.
      const isCompanyTenant =
        user?.employment_status === constants.company_tenant;
      const requiredVatQuarters = isCompanyTenant
        ? getRequiredVatQuarterPeriods()
        : [];
      const matchedVatQuarterIndexes = new Set();
      const resolveVatQuarter = (tenantDoc) => {
        const fileName = tenantDoc?.file_name;
        const analysisDoc = matchedDocuments.find(
          (doc) => getFileNameFromUrl(doc?.data?.url ?? doc?.url) === fileName
        );
        const forensicDoc = forensicByFile[fileName];
        return {
          period_year: Number(
            analysisDoc?.period_year ?? forensicDoc?.data?.period_year
          ),
          period_quarter: Number(
            analysisDoc?.period_quarter ??
              analysisDoc?.data?.period_quarter ??
              forensicDoc?.data?.period_quarter
          ),
        };
      };

      const reviewFileNames = new Set(
        buildFilesToReview(loadedAnalysisResponse, user).map(
          (file) => file.fileName
        )
      );

      tenantDocsData.map((docs) => {
        const fileName = docs?.file_name;
        const isKyc = isKycTenantDoc(docs);
        const isAnalysisDuplicate = duplicateAnalysisFileNames.has(fileName);
        const isReviewFile = reviewFileNames.has(fileName);
        const isPayslipType = docs?.type?.toLowerCase()?.includes("payslip");

        if (isKyc) return;
        if (isAnalysisDuplicate) return;
        if (docs?.from !== "clave" && isReviewFile) return;

        if (isPayslipType) {
          const resolvedPeriod = resolvePayslipPeriod(docs);
          let docWithPeriod = docs;
          if (resolvedPeriod.period_year && resolvedPeriod.period_month) {
            docWithPeriod = {
              ...docs,
              period_year: resolvedPeriod.period_year,
              period_month: resolvedPeriod.period_month,
            };
          } else if (documentPeriodMap.size > 0) {
            const periodInfo = documentPeriodMap.get(docs.file_name);
            if (periodInfo) {
              docWithPeriod = {
                ...docs,
                period_year: periodInfo.period_year,
                period_month: periodInfo.period_month,
              };
            }
          }

          if (isThreePayslipRequired) {
            const payslipPeriodYear = docWithPeriod?.period_year;
            const payslipPeriodMonth = docWithPeriod?.period_month;
            let matchedPeriodIndex = -1;

            if (
              isValidPayslipPeriod(payslipPeriodYear, payslipPeriodMonth) &&
              requiredPayslipPeriods.length > 0
            ) {
              matchedPeriodIndex = requiredPayslipPeriods.findIndex((period) =>
                isPayslipWithinRequiredPeriod(
                  {
                    period_year: payslipPeriodYear,
                    period_month: payslipPeriodMonth,
                  },
                  [period]
                )
              );
            }

            if (
              matchedPeriodIndex >= 0 &&
              !matchedPeriodIndexes.has(matchedPeriodIndex)
            ) {
              matchedPeriodIndexes.add(matchedPeriodIndex);
              type_map.set(
                PAYSLIP_PERIOD_KEYS[matchedPeriodIndex],
                docWithPeriod
              );
            } else {
              payslips.push(docWithPeriod);
              if (
                isValidPayslipPeriod(
                  docWithPeriod?.period_year,
                  docWithPeriod?.period_month
                )
              ) {
                const warning = getPayslipDateWarning(
                  docWithPeriod,
                  requiredPayslipPeriods,
                  _t
                );
                if (warning) {
                  payslipValidationMap.set(
                    docs?._id || docs?.file_name,
                    warning
                  );
                }
              }
            }
          } else {
            payslips.push(docWithPeriod);
          }
          return;
        }
        if (isCompanyTenant && docs?.type === constants.quarterly_vat) {
          const { period_year, period_quarter } = resolveVatQuarter(docs);
          const quarterIndex = requiredVatQuarters.findIndex(
            (period) =>
              period.period_year === period_year &&
              period.period_quarter === period_quarter
          );
          if (
            quarterIndex >= 0 &&
            !matchedVatQuarterIndexes.has(quarterIndex)
          ) {
            matchedVatQuarterIndexes.add(quarterIndex);
            type_map.set(VAT_QUARTER_KEYS[quarterIndex], docs);
            return;
          }
        }
        if (docs?.from == "clave") {
          temp_map.set(docs?.type);
          setHaveClavePinDoc(true);
        }
        if (
          !type_map.has(docs?.type + (docs?.year ? docs?.year : "")) &&
          docs?.from != constants.others
        ) {
          type_map.set(docs?.type + (docs?.year ? docs?.year : ""), docs);
        }
        if (docs?.from == "others" && !other_type?.has(docs?.type)) {
          other_type.set(docs?.type, docs);
        }
      });

      payslips = payslips.sort(
        (a, b) => new Date(a.created_at) - new Date(b.created_at)
      );
      setPayslipDocs(payslips);
      setDocFound(type_map);
      setOtherDocs([...other_type.values()]);
      setPayslipValidation(payslipValidationMap);
    } else {
      setPayslipDocs([]);
      setDocFound(new Map());
      setOtherDocs([]);
      setPayslipValidation(new Map());
    }
  }, [
    tenantDocsData,
    requiredPayslipPeriods,
    loadedAnalysisResponse,
    isThreePayslipRequired,
    user?._id,
  ]);

  const [documentList, setDocumentList] = useState([]);
  const [selectedDocData, setSelectedDocData] = useState({});
  const [additionalDocs, setAdditionalDocs] = useState([]);

  useEffect(() => {
    const notInDocumentList = Array.from(docFound.entries())
      .filter(([key]) => !documentList.includes(key))
      .map(([_, value]) => value);
    setAdditionalDocs(notInDocumentList);
  }, [docFound, documentList]);

  useEffect(() => {
    if (!user) return;

    let allDocs = getDocumentsFromEmploymentType(user, true);
    setSelectedDocData(allDocs);

    let allDocsWithoutProtectedHousing = getDocumentsFromEmploymentType(user);
    let baseDocs = Object.keys(allDocsWithoutProtectedHousing);
    const finalProtectedDocs = getRequiredProtectedHousingDocs();

    const mergedDocs = [
      ...new Set([...baseDocs, ...finalProtectedDocs]),
    ].filter(
      (key) =>
        haveValue(allDocs[key]) || haveValue(docsData.PROTECTED_HOUSING[key])
    );

    setDocumentList(mergedDocs);
  }, [user]);

  useEffect(() => {
    // If we are in dummy signup flow show a static list of documents
    // instead of computing document list from employment type.
    if (isDummySignup) {
      const staticDocs = [
        "Deduction certificate",
        "Working life certificate",
        "Last annual Tax Report (Model 100)",
        "Payslip",
        "Employment contract",
      ];
      // selectedDocData is expected elsewhere; set a minimal mapping so components
      // that look up selectedDocData[docKey] won't break. We'll keep keys as the
      // display names and let DocItem use translationFromFixed when rendering.
      const mapping = {};
      staticDocs.forEach((d) => (mapping[d] = d));
      setSelectedDocData(mapping);
      setDocumentList(staticDocs);
      return;
    }
  }, []);

  const handleClave = () => {
    if (isDummySignup) {
      setShowClaveModal(true);
    } else {
      handleInitiateClave();
    }
  };

  // ********** clave functionality **********
  const handleInitiateClave = async (e) => {
    if (claveInitiating) return;
    setClaveInitiating(true);
    setWaitingJobId(null);

    let temp_clave_models = {
      AEAT_100_RENTA: false,
      AEAT_130_IRPF_DIRECTA: false,
      AEAT_303_IVA_AUTOLIQUIDACION: false,
      SEG_SOCIAL_VIDA_LABORAL: false,
      SEG_SOCIAL_CERT_INTEGRAL_PRESTACIONES: false,
      SEG_SOCIAL_BASE_COTIZACIONES: false,
    };

    if (user?.employment_status == constants.employed) {
      temp_clave_models = {
        ...temp_clave_models,
        SEG_SOCIAL_BASE_COTIZACIONES: true,
      };
    }
    if (
      user?.employment_status == constants.employed ||
      user?.employment_status == constants.unemployed ||
      user?.employment_status == constants.public_servant
    ) {
      temp_clave_models = {
        ...temp_clave_models,
        SEG_SOCIAL_VIDA_LABORAL: true,
        AEAT_100_RENTA: true,
      };
    }
    if (user?.employment_status == constants.self_employed) {
      temp_clave_models = {
        ...temp_clave_models,
        SEG_SOCIAL_VIDA_LABORAL: true,
        AEAT_100_RENTA: true,
        AEAT_130_IRPF_DIRECTA: true,
        SEG_SOCIAL_BASE_COTIZACIONES: true,
      };
    }
    if (
      user?.employment_status == constants.self_employed &&
      user?.employment_category == "freelancer"
    ) {
      temp_clave_models = {
        ...temp_clave_models,
        AEAT_303_IVA_AUTOLIQUIDACION: true,
        SEG_SOCIAL_BASE_COTIZACIONES: true,
      };
    }

    if (
      user?.employment_status == constants.retired ||
      user?.employment_status == constants.unemployed
    ) {
      temp_clave_models = {
        ...temp_clave_models,
        SEG_SOCIAL_CERT_INTEGRAL_PRESTACIONES: true,
      };
    }

    const coTenantRedirect = co_tenant_a_id
      ? `co-tenant/onboarding?co_tenant_a_id=${co_tenant_a_id}&claveDone=true`
      : "co-tenant/onboarding?claveDone=true";
    initiateBankFlip(
      temp_clave_models,
      false,
      user?._id,
      reqFor === "updateDoc"
        ? redirectUrl
        : isGuarantor
          ? `guarantor/onboarding?guarantor_a_id=${guarantor_a_id}&claveDone=true`
          : isCoTenant
            ? coTenantRedirect
            : "onboarding?claveDone=true&waiting=",
      needWaitingJobForClave
    )
      .then((res) => {
        setClaveWidgetUrl(res.success_res.widgetLink);
        setWaitingJobId(res.success_res.waiting_id);
      })
      .catch((err) => {
        toast.error(_t("errorWhileInitiatingTheSignatureProcess"));
        trackClarityEvent("tenant_bankflip_error");
      })
      .finally(() => setClaveInitiating(false));
  };

  const handleClose = () => {
    setClaveWidgetUrl(null);
  };

  const handleSuccess = (data) => {
    startWaitingJobInterval(waitingJobId);
    toast.success(_t("bankflipSuccess"));
  };

  const handleError = (data) => {
    toast.success(
      _t("bankflipFailure", "When bankflip give any failure response/message")
    );
  };
  // ********** clave functionality **********

  const handleInitiateUpload = () => {
    const claveDocs = [];
    documentList.map((docItem, index) => {
      if (docsData.CLAVE_DOCS.includes(selectedDocData?.[docItem])) {
        claveDocs.push(selectedDocData?.[docItem]);
      }
    });
    if (
      claveDocs.length > 0 &&
      user?.clave_done != true &&
      isFirstTime &&
      !isGuarantor
    ) {
      setShowApprovalPopup(true);
      setAvailableClaveDocs(claveDocs);
    } else {
      handleUploadFiles();
    }
  };

  const onhandleManualUpload = () => {
    setShowApprovalPopup(false);
    const url = new URL(window.location.href);
    url.search = ""; // ⛔️ Clear all query parameters
    window.history.pushState({}, "", url.toString());
    handleUploadFiles();
  };

  // Marks a forward submit from Required (step 3) so Missing (step 4) can
  // tell a fresh submit apart from a page refresh while analysis loads.
  const markSubmittedFromRequired = () => {
    if (reqFor !== "updateDoc") {
      try {
        sessionStorage.setItem("docAnalysisFromRequired", "1");
      } catch {}
    }
  };

  function removeWelcomeParam() {
    const url = new URL(window.location.href);

    if (url.searchParams.has("welcome")) {
      setShowWelcomePopup(false);
      url.searchParams.delete("welcome");
      window.history.replaceState(
        {},
        document.title,
        url.pathname + url.search
      );
    }
  }

  const removeValidFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const removeInvalidFile = (index) => {
    setInvalidFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const requiredDocumentCount = documentList.length;
  const identifiedDocumentCount = documentList.filter((requiredDocument) =>
    Array.from(docFound.entries()).some(
      ([key, document]) =>
        key === requiredDocument ||
        (docsMap[document?.type] ?? document?.type) === requiredDocument
    )
  ).length;
  const missingDocumentCount = Math.max(
    requiredDocumentCount - identifiedDocumentCount,
    0
  );

  const claveDocumentCount = documentList.filter((docItem) =>
    docsData.CLAVE_DOCS.includes(selectedDocData?.[docItem])
  ).length;

  const isRequiredDocUploaded = (docKey) => {
    if (docFound.has(docKey)) return true;
    const aliasMatch = Object.entries(docsMap).find(
      ([key, value]) => value === docKey && docFound.has(key)
    );
    if (aliasMatch) return true;
    if (payslipDocs.some((doc) => doc?.type === docKey)) return true;
    return Array.from(docFound.entries()).some(
      ([key, document]) =>
        key === docKey || (docsMap[document?.type] ?? document?.type) === docKey
    );
  };

  const sortedDocumentList = useMemo(
    () =>
      [...documentList].sort(
        (a, b) =>
          Number(isRequiredDocUploaded(b)) - Number(isRequiredDocUploaded(a))
      ),
    // eslint-disable-next-line
    [documentList, docFound, payslipDocs]
  );
  const analysisReviewFiles = useMemo(() => {
    const files = buildFilesToReview(loadedAnalysisResponse, user);
    // filter out KYC documents completely (passport, NIE, national identity, residence permit, identification bucket, etc.)
    return files.filter(
      (file) =>
        !isKycReviewFileUtil(file, tenantDocsData) &&
        (file.bucketName || "").toLowerCase() !== "identification"
    );
  }, [loadedAnalysisResponse, user, tenantDocsData]);
  const forensicNameByFile = useMemo(() => {
    const map = new Map();
    const forensicReports =
      user?.tenant_extra_field_id?.forensic_analysis?.profileReports ?? [];
    forensicReports.forEach((entry) => {
      const keyFactors = entry?.report?.keyFactors?.keyFactors ?? [];
      const name = (Array.isArray(keyFactors) ? keyFactors : []).find(
        (kf) => kf?.type === "Name" || kf?.key === "Name"
      )?.dataInJSON?.name;
      if (!name) return;
      (entry?.report?.documents_list ?? []).forEach((doc) => {
        const fileName = getFileNameFromUrl(doc?.url);
        if (fileName && !map.has(fileName)) map.set(fileName, name);
      });
    });
    return map;
  }, [user]);

  const reviewFiles = useMemo(() => {
    const tenantDocsByFileName = new Map(
      tenantDocsData.map((doc) => [doc?.file_name, doc])
    );
    const tenantDocsUrlMap = new Map(
      tenantDocsData.map((doc) => [doc?.file_name, doc?.url])
    );
    const resolveOwnerName = (file) => {
      const tenantDoc = tenantDocsByFileName.get(file.fileName);
      const resolved = (
        file.ownerName ||
        tenantDoc?.uploadedFor ||
        forensicNameByFile.get(file.fileName) ||
        ""
      )
        .toString()
        .trim();
      return resolved;
    };
    const signedAnalysisFiles = analysisReviewFiles.map((file) => ({
      ...file,
      url: tenantDocsUrlMap.get(file.fileName) ?? null,
      displayOwnerName: resolveOwnerName(file),
    }));
    const analysisFileNames = new Set(
      analysisReviewFiles.map((file) => file.fileName)
    );
    const extraFiles = otherDocs
      .filter((doc) => !analysisFileNames.has(doc?.file_name))
      .filter((doc) => !isKycTenantDoc(doc))
      .map((doc) => ({
        id: doc?._id || doc?.file_name,
        fileName: doc?.file_name,
        tag: "extra",
        documentId: doc?._id,
        url: doc?.url ?? tenantDocsUrlMap.get(doc?.file_name) ?? null,
      }));
    const outdatedPayslipFiles = payslipDocs
      .filter((doc) => payslipValidation.has(doc?._id || doc?.file_name))
      .filter((doc) => !analysisFileNames.has(doc?.file_name))
      .map((doc) => ({
        id: doc?._id || doc?.file_name,
        fileName: doc?.file_name,
        tag: "outdatedPayslip",
        tagLabel: payslipValidation.get(doc?._id || doc?.file_name),
        documentId: doc?._id,
        url: doc?.url ?? tenantDocsUrlMap.get(doc?.file_name) ?? null,
      }));

    const files = [
      ...signedAnalysisFiles,
      ...extraFiles,
      ...outdatedPayslipFiles,
    ].filter(
      (file) =>
        !deletedReviewFileIds.includes(file.id) &&
        !isKycReviewFileUtil(file, tenantDocsData) &&
        (file.bucketName || "").toLowerCase() !== "identification" &&
        (file.tag === "outdatedPayslip" ||
          !tenantDocsData.some(
            (doc) => doc?.from === "clave" && doc?.file_name === file.fileName
          ))
    );

    return files;
  }, [
    analysisReviewFiles,
    deletedReviewFileIds,
    otherDocs,
    payslipDocs,
    payslipValidation,
    tenantDocsData,
    forensicNameByFile,
  ]);

  const thirdPartyGroups = useMemo(() => {
    const groups = new Map();
    reviewFiles
      .filter((file) => file.tag === "thirdParty")
      .forEach((file) => {
        const ownerKey =
          (file.displayOwnerName || file.ownerName || "").trim() ||
          _t("unknownOwner", "Unknown owner");
        if (!groups.has(ownerKey)) groups.set(ownerKey, []);
        groups.get(ownerKey).push(file);
      });
    return [...groups.entries()];
  }, [reviewFiles, _t]);

  const nonThirdPartyFiles = useMemo(
    () => reviewFiles.filter((file) => file.tag !== "thirdParty"),
    [reviewFiles]
  );

  const isKycReviewFile = (file) => isKycReviewFileUtil(file, tenantDocsData);

  const isDocsLoading = documentLoading || waitingJobLoading;
  const isModalBusy = isDocsLoading || docUploadLoading;

  const deleteSingleReviewFile = async (file, docsSnapshot) => {
    if (isKycReviewFile(file)) return null;

    if (file.tag === "duplicate") {
      await deleteDuplicateDocuments({
        owner: file.displayOwnerName || file.ownerName,
        documentName: file.documentName,
      });
    } else {
      const docs = docsSnapshot ?? tenantDocsData;
      const matchedDocument = docs.find(
        (doc) =>
          doc?._id === file.documentId || doc?.file_name === file.fileName
      );

      if (!matchedDocument?._id) {
        throw new Error("document-not-found");
      }

      await deleteDocument(matchedDocument._id, user?._id);
    }

    setDocsChangedHere(true);
    return file.id;
  };

  const handleDeleteReviewFile = async (file) => {
    if (isKycReviewFile(file)) return;
    try {
      setDeletingFileId(file.id);
      await deleteSingleReviewFile(file);

      setDeletedReviewFileIds((current) =>
        current.includes(file.id) ? current : [...current, file.id]
      );
      setTenantDocsData((current) =>
        current.filter((document) => document?.file_name !== file.fileName)
      );
      toast.success(_t("documentDeletedSuccessfully", "Document deleted"));
    } catch {
      toast.error(_t("errorWhileDeletingDuplicateDoc"));
    } finally {
      setDeletingFileId(null);
    }
  };

  const handleDeleteAllReviewFiles = async () => {
    const deletableFiles = reviewFiles.filter((file) => !isKycReviewFile(file));
    if (deletableFiles.length === 0 || isDeletingAllReview) return;
    try {
      setIsDeletingAllReview(true);
      const docsSnapshot = [...tenantDocsData];
      const deletedIds = [];

      for await (const file of deletableFiles) {
        try {
          const deletedId = await deleteSingleReviewFile(file, docsSnapshot);
          if (deletedId) deletedIds.push(deletedId);
        } catch {
          // continue with next file — report partial failure at the end
        }
      }

      if (deletedIds.length > 0) {
        setDeletedReviewFileIds((current) => [
          ...new Set([...current, ...deletedIds]),
        ]);
        const deletedFileNames = new Set(
          deletableFiles
            .filter((file) => deletedIds.includes(file.id))
            .map((file) => file.fileName)
        );
        setTenantDocsData((current) =>
          current.filter(
            (document) => !deletedFileNames.has(document?.file_name)
          )
        );
        toast.success(_t("documentDeletedSuccessfully", "Document deleted"));
      }
      if (deletedIds.length < deletableFiles.length) {
        toast.error(_t("errorWhileDeletingDuplicateDoc"));
      }
    } finally {
      setIsDeletingAllReview(false);
    }
  };

  const renderReviewItem = (file) => (
    <div className={styles.reviewItem} key={file.id}>
      {file.tag === "extra" ? (
        <img className={styles.reviewIcon} src="/images/file-icon.svg" alt="" />
      ) : (
        <Icon name="warning" size={16} className={styles.reviewIcon} />
      )}
      {file.url ? (
        <a
          className={styles.reviewFileName}
          href={file.url}
          target="_blank"
          rel="noopener noreferrer"
          title={removeUuidFromFileName(file.fileName)}
        >
          {removeUuidFromFileName(file.fileName)}
        </a>
      ) : (
        <span
          className={styles.reviewFileName}
          title={removeUuidFromFileName(file.fileName)}
        >
          {removeUuidFromFileName(file.fileName)}
        </span>
      )}
      <Badge tone="neutral">
        {file.tagLabel
          ? file.tagLabel
          : file.tag === "extra"
            ? _t("reviewTagExtra", "Extra file tag")
            : _t(REVIEW_TAGS[file.tag], "File review tag in onboarding")}
      </Badge>
      {!isKycReviewFile(file) ? (
        <Button
          variant="dangerGhost"
          size="sm"
          iconOnly
          iconStart={<Icon name="trash" size={16} />}
          onClick={() => handleDeleteReviewFile(file)}
          disabled={deletingFileId === file.id || isDeletingAllReview}
          aria-label={_t("deleteDocument", "Delete document")}
        />
      ) : (
        <div></div>
      )}
    </div>
  );

  const renderDocumentItem = (docItem, index, translationFromFixed = false) => {
    const docName = translationFromFixed
      ? docItem?.type
      : selectedDocData?.[docItem];
    if (!haveValue(docName) && !haveValue(docItem?.type)) return null;

    // The raw system document type/name (e.g. "Working life certificate"),
    // used to resolve tooltip copy via documentGlossaryMap regardless of
    // which rendering path (fixed vs. employment-type-derived) produced it.
    const rawDocType = translationFromFixed ? docItem?.type : docItem;

    return (
      <DocItem
        key={docItem?._id || docItem?.file_name || docItem || index}
        rawDocType={rawDocType}
        docFound={docFound}
        docItem={
          translationFromFixed
            ? docItem
            : docFound.has(docItem)
              ? docFound.get(docItem)
              : (() => {
                  const match = Object.entries(docsMap).find(
                    ([key, value]) => value === docItem && docFound.has(key)
                  );
                  return match
                    ? docFound.get(match[0])
                    : payslipDocs.find((doc) => doc.type === docItem)
                      ? docItem
                      : null;
                })()
        }
        selectedDocData={selectedDocData}
        docsData={docsData}
        docName={
          translationFromFixed ? docItem?.type : selectedDocData?.[docItem]
        }
        translationFromFixed={translationFromFixed}
        handleInitiateClave={handleInitiateClave}
        index={index}
        haveClavePinDoc={haveClavePinDoc}
        showWelcomePopup={showWelcomePopup}
        hideClave={hideClave}
        payslipWarning={
          translationFromFixed && isThreePayslipRequired
            ? payslipValidation.get(docItem?._id || docItem?.file_name)
            : null
        }
      />
    );
  };

  const uploadFileCount = selectedFiles.length + invalidFiles.length;
  const uploadUpwardOffset = Math.min(
    Math.max(uploadFileCount - 2, 0) * 32,
    80
  );

  return (
    <>
      <DisableZoom />
      <div
        className={["popup-overlay", "wrapper-modal", styles.overlay].join(" ")}
      >
        <div
          className={["required-doc-popup", styles.card].join(" ")}
          onClick={
            isGuarantor
              ? (event) => {
                  event.stopPropagation();
                  removeWelcomeParam();
                }
              : removeWelcomeParam
          }
        >
          {isModalBusy ? (
            <div
              className={styles.uploadingState}
              role="status"
              aria-live="polite"
              aria-label={_t("loading", "Loading")}
            >
              <img src={SkorDefaultLoader} alt={_t("loading", "Loading")} />
              {isUploadingFiles && <p>{_t("uploading", "Uploading..")}</p>}
            </div>
          ) : inlineStep && claveWidgetUrl ? (
            <PermissoWidget
              inline
              widgetUrl={claveWidgetUrl}
              onClose={handleClose}
              onSuccess={handleSuccess}
              onError={handleError}
            />
          ) : (
            <>
              {(isGuarantor || isEditMode) && (
                <Button
                  variant="ghost"
                  iconOnly
                  iconStart={<Icon name="close" />}
                  className={styles.closeButton}
                  onClick={() => (onClose ? onClose() : navigate("/home"))}
                  aria-label={_t("close")}
                />
              )}
              <header className={styles.header}>
                <h1>{_t("requiredDocuments", "Your documents")}</h1>
                <p>
                  {_t(
                    "uploadMoreOrOtherIfYouDontHaveAll",
                    "We need  documents to verify your solvency. Upload them straight from your computer, or use Cl@ve to import the ones available automatically. If any are missing, you can add them later."
                  )}
                </p>
              </header>
              <div className={styles.contentGrid}>
                <div className={styles.requirements}>
                  <Badge tone="neutral" className={styles.countBadge}>
                    {_t(
                      "requiredDocumentsCountBadge",
                      "Identified count badge",
                      "translation",
                      {
                        identified: identifiedDocumentCount,
                        required: requiredDocumentCount,
                      }
                    )}
                  </Badge>
                  <div className={styles.documentList}>
                    {isDocsLoading ? (
                      <div className={styles.listSkeleton} aria-hidden="true">
                        <Skeleton height={56} radius="md" />
                        <Skeleton height={56} radius="md" />
                        <Skeleton height={56} radius="md" />
                        <Skeleton height={56} radius="md" />
                      </div>
                    ) : (
                      <>
                        <div className={styles.documentItems}>
                          {sortedDocumentList.map((docItem, index) =>
                            renderDocumentItem(docItem, index)
                          )}
                          {payslipDocs.map((docItem, index) =>
                            renderDocumentItem(docItem, index, true)
                          )}
                          {additionalDocs.map((docItem, index) =>
                            renderDocumentItem(docItem, index, true)
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div
                  className={[
                    styles.uploadColumn,
                    uploadUpwardOffset > 0 ? styles.expandedUploadColumn : "",
                  ].join(" ")}
                  style={
                    uploadUpwardOffset > 0
                      ? { "--upload-upward-offset": `-${uploadUpwardOffset}px` }
                      : undefined
                  }
                >
                  {docUploadLoading ? (
                    <div role="status" aria-label={_t("loading", "Loading")}>
                      <Skeleton height={308} radius="lg" />
                    </div>
                  ) : (
                    <div
                      className={[
                        styles.uploadCard,
                        isDragging ? styles.dragging : "",
                      ].join(" ")}
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onDragEnter={handleDragEnter}
                      onDragLeave={handleDragLeave}
                    >
                      <FileInput
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        className={styles.fileInput}
                        accept="application/pdf, image/jpeg, image/png, image/jpg"
                        multiple
                      />

                      {selectedFiles.length > 0 ? null : (
                        <>
                          <div className={styles.uploadIcon} aria-hidden="true">
                            <Icon name="upload" />
                          </div>
                          <h2>{_t("dragAndDropYourDocumentsHere")}</h2>
                          <p className={styles.uploadHint}>
                            {_t(
                              "uploadHintDropSeveral",
                              "Upload hint for drag and drop"
                            )}
                          </p>
                        </>
                      )}

                      {(selectedFiles.length > 0 ||
                        invalidFiles.length > 0) && (
                        <div className={styles.uploadedList}>
                          {invalidFiles.map((file, index) => (
                            <div
                              key={file.name + index}
                              className={[
                                styles.uploadedFile,
                                styles.invalidFile,
                              ].join(" ")}
                            >
                              <div className={styles.fileName}>
                                <img src="/images/docIconGreen.svg" alt="" />
                                <span>{file.name}</span>
                              </div>
                              <span className={styles.fileError}>
                                {file.reason}
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                iconOnly
                                iconStart={<Icon name="close" size={16} />}
                                onClick={() => removeInvalidFile(index)}
                                aria-label={_t("remove", "Remove")}
                              />
                            </div>
                          ))}
                          {selectedFiles.map((file, index) => {
                            const sizeInKB = file.size / 1024;
                            const sizeText =
                              sizeInKB < 1024
                                ? sizeInKB.toFixed(1) + " KB"
                                : (sizeInKB / 1024).toFixed(2) + " MB";

                            return (
                              <div
                                key={file.name + index}
                                className={styles.uploadedFile}
                              >
                                <div className={styles.fileName}>
                                  <img src="/images/docIconGreen.svg" alt="" />
                                  <span>{file.name}</span>
                                </div>
                                <span className={styles.fileSize}>
                                  {sizeText}
                                </span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  iconOnly
                                  iconStart={<Icon name="close" size={16} />}
                                  onClick={() => removeValidFile(index)}
                                  aria-label={_t("remove", "Remove")}
                                />
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* once files are picked, Next is the main action */}
                      <Button
                        variant={
                          selectedFiles.length > 0 ? "outline" : "primary"
                        }
                        size="lg"
                        block
                        className={styles.selectButton}
                        onClick={handleClick}
                      >
                        {selectedFiles.length > 0
                          ? _t("addMoreFiles", "Add more files")
                          : _t("selectFiles", "Select files button")}
                      </Button>

                      {!hideClave &&
                        user?.employment_status !== "student" &&
                        user?.employment_status !== constants.company &&
                        user?.employment_status !==
                          constants.company_tenant && (
                          <>
                            <Button
                              variant="outline"
                              size="lg"
                              block
                              className={styles.claveButton}
                              loading={claveInitiating}
                              onClick={handleInitiateClave}
                            >
                              {_t(
                                "importWithClave",
                                "Import with Cl@ve button"
                              )}
                            </Button>
                            <p className={styles.claveHint}>
                              <svg
                                className={styles.sparklesIcon}
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                              >
                                <path d="M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z" />
                                <path d="M20 2v4" />
                                <path d="M22 4h-4" />
                                <circle cx="4" cy="20" r="2" />
                              </svg>
                              {_t(
                                "claveFetchesDocumentsHint",
                                "Cl@ve fetches hint",
                                "translation",
                                {
                                  claveCount: claveDocumentCount,
                                  required: requiredDocumentCount,
                                }
                              )}
                            </p>
                          </>
                        )}
                    </div>
                  )}

                  <p className={styles.reassurance}>
                    <Icon name="check" size={18} />
                    {_t(
                      "reassuranceNoNeedToSort",
                      "Reassurance text for upload"
                    )}
                  </p>
                </div>
              </div>

              {!documentLoading &&
                !waitingJobLoading &&
                reviewFiles.length > 0 && (
                  <details className={styles.reviewFullWidth} open>
                    <summary>
                      <span>
                        {_t(
                          "reviewFilesDetectedTitle",
                          "Other files detected title",
                          "translation",
                          { count: reviewFiles.length }
                        )}
                      </span>
                      <Button
                        variant="danger"
                        size="sm"
                        className={styles.deleteAllButton}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDeleteAllReviewFiles();
                        }}
                        disabled={
                          isDeletingAllReview || deletingFileId !== null
                        }
                      >
                        {isDeletingAllReview
                          ? _t("deleting", "Deleting..")
                          : _t("deleteAll", "Delete all")}
                      </Button>
                    </summary>
                    <p>
                      {_t(
                        "reviewFilesDetectedDescription",
                        "Other files detected description"
                      )}
                    </p>
                    <div className={styles.reviewGroupsGrid}>
                      {thirdPartyGroups.map(([ownerName, files]) => (
                        <div
                          className={styles.reviewGroup}
                          key={`thirdParty-${ownerName}`}
                        >
                          <p className={styles.reviewOwner}>
                            {_t(
                              "thirdPartyDocumentsOf",
                              "Third party documents owner heading",
                              "translation",
                              { owner: ownerName }
                            )}
                          </p>
                          {files.map((file) => renderReviewItem(file))}
                        </div>
                      ))}
                      {nonThirdPartyFiles.length > 0 && (
                        <div className={styles.reviewGroup}>
                          {nonThirdPartyFiles.map((file) =>
                            renderReviewItem(file)
                          )}
                        </div>
                      )}
                    </div>
                  </details>
                )}

              <div className={styles.actions}>
                {reqFor === "updateDoc" && (
                  <Button
                    variant="secondary"
                    size="lg"
                    className={styles.secondaryAction}
                    onClick={onClose}
                  >
                    {_t("close")}
                  </Button>
                )}
                <div
                  data-tooltip-id="button-action-tooltip"
                  className={styles.actionTooltip}
                >
                  <Button
                    variant="primary"
                    size="lg"
                    block
                    onClick={handleInitiateUpload}
                    loading={docUploadLoading}
                    disabled={
                      (docFound.size === 0 &&
                        selectedFiles.length === 0 &&
                        !canProceedWithAnalysis) ||
                      (user?.re_new_pending && selectedFiles.length === 0) ||
                      invalidFiles.length > 0
                    }
                  >
                    {reqFor === "updateDoc" ? _t("submit") : _t("next")}
                  </Button>
                  {docUploadLoading ||
                    (docFound.size === 0 &&
                      selectedFiles.length === 0 &&
                      !canProceedWithAnalysis) ||
                    (invalidFiles.length > 0 && <ButtonActionTooltip />)}
                </div>
              </div>
            </>
          )}
        </div>

        <ApprovedModal
          showPopup={showApprovalPopup}
          setShowApprovalPopup={setShowApprovalPopup}
          availableClaveDocs={availableClaveDocs}
          onhandleManualUpload={onhandleManualUpload}
          handleInitiateClave={handleInitiateClave}
        />
        {claveWidgetUrl && !inlineStep && (
          <PermissoWidget
            widgetUrl={claveWidgetUrl}
            onClose={handleClose}
            onSuccess={handleSuccess}
            onError={handleError}
          />
        )}
      </div>
      {/* <DocumentAnalysisLoadingPopup
        isGuarantor={isGuarantor}
        showPopup={docUploadLoading}
      /> */}
    </>
  );
};

export default RequiredDocumentsModal;
